// src/pages/Admin/Overview.tsx
import React from 'react';
import { 
  TrendingUp, 
  ShoppingCart, 
  Users, 
  Package,
  AlertCircle,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { formatCurrency } from '../../utils/currencyFormatter';

const Overview: React.FC = () => {
  // This would be replaced with real data from API
  const stats = [
    {
      name: 'Total Orders',
      value: '2,543',
      change: '+12.3%',
      trend: 'up',
      icon: ShoppingCart,
      color: 'bg-blue-500'
    },
    {
      name: 'Revenue',
      value: '$45,234',
      change: '+8.2%',
      trend: 'up',
      icon: TrendingUp,
      color: 'bg-green-500'
    },
    {
      name: 'Active Users',
      value: '1,287',
      change: '+5.7%',
      trend: 'up',
      icon: Users,
      color: 'bg-purple-500'
    },
    {
      name: 'Low Stock Items',
      value: '23',
      change: '-2.1%',
      trend: 'down',
      icon: AlertCircle,
      color: 'bg-orange-500'
    },
  ];

  const recentOrders = [
    { id: 'ORD-001', customer: 'John Doe', total: '$129.99', status: 'delivered', date: '2024-01-15' },
    { id: 'ORD-002', customer: 'Jane Smith', total: '$79.99', status: 'processing', date: '2024-01-15' },
    { id: 'ORD-003', customer: 'Bob Johnson', total: '$249.99', status: 'pending', date: '2024-01-14' },
    { id: 'ORD-004', customer: 'Alice Brown', total: '$34.99', status: 'shipped', date: '2024-01-14' },
    { id: 'ORD-005', customer: 'Charlie Wilson', total: '$599.99', status: 'delivered', date: '2024-01-13' },
  ];

  const getStatusColor = (status: string) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
        <div className="text-sm text-gray-500">
          Last updated: {new Date().toLocaleDateString()}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.name}
              className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div className={`p-3 rounded-lg ${stat.color} bg-opacity-10`}>
                  <Icon className={`h-6 w-6 ${stat.color.replace('bg-', 'text-')}`} />
                </div>
                <div className={`flex items-center text-sm ${
                  stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {stat.trend === 'up' ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
                  <span>{stat.change}</span>
                </div>
              </div>
              <div className="mt-4">
                <p className="text-sm text-gray-600">{stat.name}</p>
                <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900">Recent Orders</h2>
          <button className="text-sm text-blue-600 hover:text-blue-700">
            View All
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {order.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {order.customer}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(order.total)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(order.status)}`}>
                      {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {order.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button className="text-blue-600 hover:text-blue-900">
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <button className="w-full text-left px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-700">
              Create new product
            </button>
            <button className="w-full text-left px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-700">
              Process pending orders
            </button>
            <button className="w-full text-left px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-700">
              Update inventory
            </button>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Inventory Alert</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Product A - Size M</span>
              <span className="text-sm font-medium text-orange-600">3 left</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Product B - Black</span>
              <span className="text-sm font-medium text-orange-600">2 left</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Product C - 1kg</span>
              <span className="text-sm font-medium text-red-600">1 left</span>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Today's Summary</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Orders</span>
              <span className="text-sm font-medium text-gray-900">24</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Revenue</span>
              <span className="text-sm font-medium text-gray-900">$2,345</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">New customers</span>
              <span className="text-sm font-medium text-gray-900">8</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview;